import type { Tone as RuleTone } from '@oms/contracts';
import { humanize } from '../lib/format';

/**
 * Semantic status tones (see tokens.css). "danger" is kept as an alias of "critical" for older
 * callers. Colour is never the only signal: every badge carries its text label.
 */
export type Tone = RuleTone | 'danger';

const TONES: Record<string, Tone> = {
  // success / completed
  active: 'success',
  verified: 'success',
  received: 'success',
  approved: 'success',
  paid: 'success',
  reconciled: 'success',
  delivered: 'success',
  delivered_on_time: 'success',
  fulfilled: 'success',
  accepted: 'success',
  resolved: 'success',
  // information / in progress
  current: 'info',
  trialing: 'info',
  invited: 'info',
  in_transit: 'info',
  dispatched: 'info',
  out_for_delivery: 'info',
  in_fulfilment: 'info',
  on_track: 'info',
  issued: 'info',
  acknowledged: 'info',
  partially_received: 'info',
  // warning / approaching a deadline
  due_soon: 'warning',
  at_risk: 'warning',
  delayed: 'warning',
  delivery_attempted: 'warning',
  delivered_late: 'warning',
  approaching: 'warning',
  past_due: 'warning',
  unverified: 'warning',
  disputed: 'warning',
  // critical / overdue
  overdue: 'critical',
  missed: 'critical',
  exception: 'critical',
  returned: 'critical',
  failed: 'critical',
  rejected: 'critical',
  blocked: 'critical',
  suspended: 'critical',
  expired: 'critical',
  // pending / awaiting action
  pending: 'pending',
  pending_review: 'pending',
  pending_approval: 'pending',
  pending_confirmation: 'pending',
  awaiting_approval: 'pending',
  partially_paid: 'pending',
  unreconciled: 'pending',
  expected: 'pending',
  open: 'pending',
  // neutral / inactive
  draft: 'neutral',
  closed: 'neutral',
  disabled: 'neutral',
  inactive: 'neutral',
  revoked: 'neutral',
  cancelled: 'neutral',
  reversed: 'neutral',
  superseded: 'neutral',
  not_due: 'neutral',
  no_commitment: 'neutral',
  unknown: 'neutral',
  not_required: 'neutral',
};

export function toneFor(status: string): Tone {
  return TONES[status] ?? 'neutral';
}

export function toneClass(tone: Tone): string {
  const t = tone === 'danger' ? 'critical' : tone;
  return t === 'neutral' ? 'badge' : `badge badge-${t}`;
}

export function StatusBadge({ status, label, tone, title }: { status: string; label?: string; tone?: Tone; title?: string }) {
  const t = tone ?? toneFor(status);
  return (
    <span className={toneClass(t)} title={title}>
      {label ?? humanize(status)}
    </span>
  );
}
